import { useState, type ReactNode } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { Route as rootRoute } from './__root.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Label } from '../components/ui/Label.js';
import { ApiError, resetPassword } from '../lib/api.js';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$token',
  component: ResetPasswordPage,
});

/** Brand chrome shared with the signup / forgot-password pages. */
function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3 justify-center">
          <div className="h-9 w-9 rounded-lg bg-navy flex items-center justify-center">
            <span className="font-serif text-cream text-lg leading-none">A</span>
          </div>
          <div className="font-serif text-2xl tracking-tighter2 text-ink">Admitly</div>
        </div>
        <div className="bg-white border border-line rounded-2xl shadow-card p-8">{children}</div>
      </div>
    </div>
  );
}

function ResetPasswordPage() {
  const { token } = Route.useParams();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Client-side checks mirror the server policy (min 8) and the confirm field.
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords don’t match.');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts just now — please try again in a minute.');
      } else {
        // Any other failure means the link is invalid / expired / already used.
        setLinkInvalid(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">Password reset</h1>
          <p className="text-sm text-muted mb-6">
            Your password has been updated and you&rsquo;ve been signed out everywhere else. Sign
            in with your new password to continue.
          </p>
          <Link to="/login">
            <Button className="w-full">Go to login</Button>
          </Link>
        </div>
      </Shell>
    );
  }

  if (linkInvalid) {
    return (
      <Shell>
        <div className="text-center">
          <h1 className="font-serif text-2xl text-ink tracking-tight mb-2">
            This reset link is invalid or has expired
          </h1>
          <p className="text-sm text-muted mb-6">
            Reset links can only be used once and expire after an hour. Request a fresh one to try
            again.
          </p>
          <Link to="/forgot-password">
            <Button className="w-full">Request a new link</Button>
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="font-serif text-2xl text-ink tracking-tight mb-1">Set a new password</h1>
      <p className="text-sm text-muted mb-6">Choose a new password for your Admitly account.</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="reset-password">New password</Label>
          <Input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
          />
          <p className="text-xs text-muted mt-1.5">At least 8 characters.</p>
        </div>

        <div>
          <Label htmlFor="reset-confirm">Confirm new password</Label>
          <Input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••••"
          />
        </div>

        {error && (
          <div className="rounded-lg bg-danger-bg border border-danger/20 px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <Button type="submit" size="lg" loading={submitting} className="w-full mt-2">
          Reset password
        </Button>
      </form>

      <div className="mt-6 text-center text-xs text-muted">
        <Link to="/login" className="text-navy hover:underline">
          Back to login
        </Link>
      </div>
    </Shell>
  );
}
